/**
 * Battle Lobby Page
 *
 * Central hub for finding and creating battles.
 * Follows L3 specification: battle-lobby-system.md
 *
 * OCBV Update: Supports open/closed battles with password protection
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { OnlinePlayer } from '@drawn-of-war/shared';
import { createBattle, joinBattle, quickPlay } from '../services/battle-api';
import {
  InsufficientCreaturesError,
  ActiveBattleExistsError,
  BattleNotFoundError,
  BattleAlreadyFilledError,
  CannotJoinOwnBattleError,
  IncorrectPasswordError,
  PasswordRequiredError
} from '../services/battle-api-errors';
import { useLobbySocket } from '../hooks/useLobbySocket';
import type { LobbyBattle } from '../services/lobby-socket';
import { PasswordJoinModal } from '../components/PasswordJoinModal';
import { OnlinePlayersList } from '../components/OnlinePlayersList';
import { PlayerProfileModal } from '../components/PlayerProfileModal';
import { TradeModal } from '../components/TradeModal';
import { PlayerSearch } from '../components/PlayerSearch';
import { RecentBattlesSection } from '../components/RecentBattlesSection';
import { CommunityBattleFeed } from '../components/CommunityBattleFeed';
import type { SearchResultPlayer } from '../hooks/usePlayerSearch';
import { PageAtmosphere } from '../components/atmosphere';
import { NineSliceBorder } from '../components/atmosphere/NineSliceBorder';
import styles from './BattleLobbyPage.module.css';

/**
 * SVG Icons matching HomePage style
 */
function QuickPlayIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  );
}

function CreateBattleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="16" />
      <line x1="8" y1="12" x2="16" y2="12" />
    </svg>
  );
}

function BrowseIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

/**
 * Ornate pixel art divider between sections
 */
function OrnateDivider() {
  return (
    <div className={styles.ornateDivider}>
      <img
        src="/assets/UI/pixel-borders/divider-ornate.png"
        alt=""
        className={styles.ornateDividerImg}
      />
    </div>
  );
}

export function BattleLobbyPage() {
  const navigate = useNavigate();

  // Create Battle Modal state
  const [battleName, setBattleName] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [creatureLimit, setCreatureLimit] = useState<number>(3);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Quick Play state
  const [isQuickPlaying, setIsQuickPlaying] = useState(false);
  const [quickPlayError, setQuickPlayError] = useState<string | null>(null);

  // Battle list join state
  const [joiningBattleId, setJoiningBattleId] = useState<string | null>(null);
  const [listJoinError, setListJoinError] = useState<string | null>(null);

  // Password modal state
  const [passwordModalBattle, setPasswordModalBattle] = useState<LobbyBattle | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false);

  // Player profile modal state
  const [selectedPlayer, setSelectedPlayer] = useState<OnlinePlayer | null>(null);
  const [showTradeModal, setShowTradeModal] = useState(false);

  const handlePlayerClick = useCallback((player: OnlinePlayer) => {
    setSelectedPlayer(player);
  }, []);

  const handleSearchPlayerSelect = useCallback((player: SearchResultPlayer) => {
    // Convert SearchResultPlayer to OnlinePlayer format for the profile modal
    setSelectedPlayer({
      id: player.id,
      displayName: player.displayName,
      activity: 'browsing', // Default activity since we don't have it from search
      stats: player.stats
    });
  }, []);

  const handleCloseProfile = useCallback(() => {
    setSelectedPlayer(null);
  }, []);

  const handleOpenTrade = useCallback(() => {
    setShowTradeModal(true);
  }, []);

  const handleCloseTrade = useCallback(() => {
    setShowTradeModal(false);
  }, []);

  // Connect to lobby socket for real-time battle list
  const { battles, isConnected, error: socketError } = useLobbySocket();

  /**
   * Quick Play - Auto-match or create battle
   */
  async function handleQuickPlay() {
    setIsQuickPlaying(true);
    setQuickPlayError(null);

    try {
      const result = await quickPlay();
      navigate(`/deployment?matchId=${result.matchId}&playerId=${result.playerId}`);
      setIsQuickPlaying(false);
    } catch (error) {
      setIsQuickPlaying(false);
      if (error instanceof InsufficientCreaturesError) {
        setQuickPlayError(error.message);
      } else if (error instanceof ActiveBattleExistsError) {
        setQuickPlayError(error.message);
      } else if (error instanceof Error) {
        setQuickPlayError(error.message);
      } else {
        setQuickPlayError('Failed to find a battle. Please try again.');
      }
    }
  }

  /**
   * Create Battle - Now supports open/closed with password
   */
  async function handleCreateBattle() {
    if (!battleName.trim()) {
      setCreateError('Please enter a battle name');
      return;
    }

    // Validate password for closed battles
    if (!isPublic && password.length < 4) {
      setCreateError('Password must be at least 4 characters');
      return;
    }

    setIsCreating(true);
    setCreateError(null);

    try {
      const result = await createBattle(
        battleName,
        isPublic,
        isPublic ? undefined : password,
        creatureLimit
      );
      navigate(`/deployment?matchId=${result.matchId}&playerId=${result.playerId}`);
      resetCreateModal();
    } catch (error) {
      setIsCreating(false);
      if (error instanceof InsufficientCreaturesError) {
        setCreateError(error.message);
      } else if (error instanceof ActiveBattleExistsError) {
        setCreateError(error.message);
      } else if (error instanceof Error) {
        setCreateError(error.message);
      } else {
        setCreateError('Failed to create battle. Please try again.');
      }
    }
  }

  /**
   * Reset create modal state
   */
  function resetCreateModal() {
    setShowCreateModal(false);
    setBattleName('');
    setIsPublic(true);
    setPassword('');
    setShowPassword(false);
    setCreatureLimit(3);
    setIsCreating(false);
    setCreateError(null);
  }

  /**
   * Join Battle from List - handles both open and closed battles
   */
  async function handleJoinFromList(battle: LobbyBattle) {
    // If closed battle, show password modal
    if (!battle.isPublic) {
      setPasswordModalBattle(battle);
      setPasswordError(null);
      return;
    }

    // Open battle - join directly
    setJoiningBattleId(battle.battleId);
    setListJoinError(null);

    try {
      const result = await joinBattle(battle.battleId);
      navigate(`/deployment?matchId=${result.matchId}&playerId=${result.playerId}`);
      setJoiningBattleId(null);
    } catch (error) {
      setJoiningBattleId(null);
      handleJoinError(error, setListJoinError);
    }
  }

  /**
   * Submit password for closed battle
   */
  async function handlePasswordSubmit(passwordValue: string) {
    if (!passwordModalBattle) return;

    setIsPasswordSubmitting(true);
    setPasswordError(null);

    try {
      const result = await joinBattle(passwordModalBattle.battleId, passwordValue);
      navigate(`/deployment?matchId=${result.matchId}&playerId=${result.playerId}`);
      setPasswordModalBattle(null);
      setIsPasswordSubmitting(false);
    } catch (error) {
      setIsPasswordSubmitting(false);
      if (error instanceof IncorrectPasswordError) {
        setPasswordError('Incorrect password');
      } else if (error instanceof PasswordRequiredError) {
        setPasswordError('Password is required');
      } else {
        handleJoinError(error, setPasswordError);
      }
    }
  }

  /**
   * Handle join errors
   */
  function handleJoinError(error: unknown, setError: (msg: string | null) => void) {
    if (error instanceof BattleNotFoundError) {
      setError(error.message);
    } else if (error instanceof BattleAlreadyFilledError) {
      setError(error.message);
    } else if (error instanceof CannotJoinOwnBattleError) {
      setError(error.message);
    } else if (error instanceof InsufficientCreaturesError) {
      setError(error.message);
    } else if (error instanceof Error) {
      setError(error.message);
    } else {
      setError('Failed to join battle. Please try again.');
    }
  }

  return (
    <PageAtmosphere page="battle">
    <div className={styles.container}>
      <h1 className={styles.title}>Battle Lobby</h1>
      <p className={styles.subtitle}>
        Find opponents, create battles, or jump into Quick Play
      </p>

      <OrnateDivider />

      {/* Main action cards - now just 2 cards */}
      <div className={styles.mainActionsGrid}>
        {/* Quick Play */}
        <div
          className={`${styles.mainActionCard} ${styles.clickableCard} ${isQuickPlaying ? styles.disabled : ''}`}
          onClick={() => !isQuickPlaying && handleQuickPlay()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && !isQuickPlaying && handleQuickPlay()}
        >
          <div className={styles.cardIcon}><QuickPlayIcon /></div>
          <h2 className={styles.cardTitle}>Quick Play</h2>
          <p className={styles.cardDescription}>
            {isQuickPlaying ? 'Searching for a match...' : 'Jump into a battle immediately'}
          </p>
          {quickPlayError && (
            <div className={styles.errorMessage}>{quickPlayError}</div>
          )}
        </div>

        {/* Create Battle */}
        <div
          className={`${styles.mainActionCard} ${styles.clickableCard}`}
          onClick={() => {
            setShowCreateModal(true);
            setCreateError(null);
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && setShowCreateModal(true)}
        >
          <div className={styles.cardIcon}><CreateBattleIcon /></div>
          <h2 className={styles.cardTitle}>Create Battle</h2>
          <p className={styles.cardDescription}>
            Host a new battle with friends
          </p>
        </div>
      </div>

      <OrnateDivider />

      {/* Browse All Battles */}
      <div className={styles.browseSection}>
        <NineSliceBorder
          cornerSrc="/assets/UI/pixel-borders/9s-dark-corner-tl.png"
          edgeTopSrc="/assets/UI/pixel-borders/9s-dark-edge-top.png"
          edgeLeftSrc="/assets/UI/pixel-borders/9s-dark-edge-left.png"
        />
        <div className={styles.browseHeader}>
          <h2 className={styles.browseTitle}>
            <span className={styles.browseTitleIcon}><BrowseIcon /></span>
            All Battles
          </h2>
          <div className={`${styles.connectionStatus} ${isConnected ? styles.connected : styles.disconnected}`}>
            {isConnected ? 'Connected' : 'Disconnected'}
          </div>
        </div>

        {socketError && (
          <div className={styles.errorMessage}>{socketError}</div>
        )}

        {listJoinError && (
          <div className={styles.errorMessage}>{listJoinError}</div>
        )}

        {battles.length > 0 && (
          <p className={styles.battleCount}>
            {battles.length} {battles.length === 1 ? 'battle' : 'battles'} available
          </p>
        )}

        {battles.length === 0 ? (
          <div className={styles.emptyState}>
            <p>No active battles</p>
          </div>
        ) : (
          <div className={styles.battleList}>
            {battles.map((battle) => {
              const isJoiningThis = joiningBattleId === battle.battleId;
              // Get creature limit from battle, default to 6 if not available
              const battleCreatureLimit = (battle as LobbyBattle & { creatureLimit?: number }).creatureLimit ?? 6;
              return (
                <div
                  key={battle.battleId}
                  role="button"
                  aria-disabled={isJoiningThis}
                  onClick={() => !isJoiningThis && handleJoinFromList(battle)}
                  className={`${styles.battleCard} ${isJoiningThis ? styles.joining : ''}`}
                >
                  {/* Visibility icon and battle name with creature limit */}
                  <div className={styles.battleHeader}>
                    <span className={styles.visibilityIcon} title={battle.isPublic ? 'Open battle' : 'Password protected'}>
                      {battle.isPublic ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter">
                          <circle cx="12" cy="12" r="10" />
                          <line x1="2" y1="12" x2="22" y2="12" />
                          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter">
                          <rect x="3" y="11" width="18" height="11" />
                          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                      )}
                    </span>
                    <h3 className={styles.battleName}>
                      {battle.battleName} <span className={styles.battleSize}>({battleCreatureLimit}v{battleCreatureLimit})</span>
                    </h3>
                  </div>

                  {/* Creator info */}
                  <p className={styles.hostInfo}>
                    by {battle.creatorDisplayName || battle.players?.host?.displayName || 'Unknown'}
                  </p>

                  {/* Join button */}
                  <button
                    className={styles.joinButton}
                    disabled={isJoiningThis}
                  >
                    {isJoiningThis ? 'Joining...' : 'Join'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Community Battle Feed - All battles in the community */}
      <div className={styles.communityFeedWrapper}>
        <CommunityBattleFeed initialLimit={5} />
      </div>

      {/* Recent Battles Section - Personal battle history with stats */}
      <div className={styles.recentBattlesWrapper}>
        <RecentBattlesSection limit={5} />
      </div>

      {/* Create Battle Modal - Updated with visibility toggle */}
      {showCreateModal && (
        <div className={styles.modalOverlay} onClick={resetCreateModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <NineSliceBorder
              cornerSrc="/assets/UI/pixel-borders/9s-dark-corner-tl.png"
              edgeTopSrc="/assets/UI/pixel-borders/9s-dark-edge-top.png"
              edgeLeftSrc="/assets/UI/pixel-borders/9s-dark-edge-left.png"
            />
            <h2 className={styles.modalTitle}>Create New Battle</h2>

            {/* Battle Name */}
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Battle Name</label>
              <input
                type="text"
                placeholder="Epic Showdown"
                value={battleName}
                onChange={(e) => setBattleName(e.target.value)}
                maxLength={50}
                autoFocus
                disabled={isCreating}
                className={styles.formInput}
              />
            </div>

            {/* Visibility Toggle */}
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Visibility</label>
              <div className={styles.visibilityToggle}>
                <label className={`${styles.visibilityOption} ${isPublic ? styles.selected : ''}`}>
                  <input
                    type="radio"
                    name="visibility"
                    checked={isPublic}
                    onChange={() => setIsPublic(true)}
                    disabled={isCreating}
                  />
                  <span className={styles.visibilityIcon}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="2" y1="12" x2="22" y2="12" />
                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                    </svg>
                  </span>
                  <div>
                    <strong>Open</strong>
                    <span className={styles.visibilityDesc}>Anyone can join</span>
                  </div>
                </label>
                <label className={`${styles.visibilityOption} ${!isPublic ? styles.selected : ''}`}>
                  <input
                    type="radio"
                    name="visibility"
                    checked={!isPublic}
                    onChange={() => setIsPublic(false)}
                    disabled={isCreating}
                  />
                  <span className={styles.visibilityIcon}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter">
                      <rect x="3" y="11" width="18" height="11" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                  </span>
                  <div>
                    <strong>Closed</strong>
                    <span className={styles.visibilityDesc}>Password required</span>
                  </div>
                </label>
              </div>
            </div>

            {/* Battle Size Selector */}
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Battle Size</label>
              <div className={styles.battleSizeToggle}>
                {[3, 4, 5, 6].map((num) => (
                  <button
                    key={num}
                    type="button"
                    className={`${styles.battleSizeOption} ${creatureLimit === num ? styles.selected : ''}`}
                    onClick={() => setCreatureLimit(num)}
                    disabled={isCreating}
                  >
                    {num}v{num}
                  </button>
                ))}
              </div>
            </div>

            {/* Password Field (only for closed battles) */}
            {!isPublic && (
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Password</label>
                <div className={styles.passwordInputWrapper}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter password (min 4 characters)"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    maxLength={32}
                    disabled={isCreating}
                    className={styles.formInput}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={styles.togglePasswordBtn}
                    title={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
              </div>
            )}

            {createError && (
              <div className={styles.errorMessage}>{createError}</div>
            )}

            <div className={styles.modalActions}>
              <button
                onClick={resetCreateModal}
                disabled={isCreating}
                className={`${styles.button} ${styles.cancel}`}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateBattle}
                disabled={isCreating || !battleName.trim() || (!isPublic && password.length < 4)}
                className={`${styles.button} ${styles.create}`}
              >
                {isCreating ? 'Creating...' : 'Create Battle'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Online Players Sidebar */}
      <aside className={styles.onlinePlayersSidebar}>
        {/* Player Search */}
        <div className={styles.playerSearchContainer}>
          <h3 className={styles.playerSearchTitle}>Find Player</h3>
          <PlayerSearch
            onPlayerSelect={handleSearchPlayerSelect}
            placeholder="Search players..."
            limit={10}
          />
        </div>

        {/* Online Players List */}
        <OnlinePlayersList
          showSearch={false}
          initialLimit={10}
          compact={false}
          onPlayerClick={handlePlayerClick}
        />
      </aside>

      {/* Player Profile Modal */}
      {selectedPlayer && (
        <PlayerProfileModal
          userId={selectedPlayer.id}
          isOpen={true}
          onClose={handleCloseProfile}
          onTrade={handleOpenTrade}
        />
      )}

      {/* Trade Modal */}
      {selectedPlayer && showTradeModal && (
        <TradeModal
          isOpen={showTradeModal}
          onClose={handleCloseTrade}
          targetUserId={selectedPlayer.id}
          targetDisplayName={selectedPlayer.displayName}
        />
      )}

      {/* Password Join Modal */}
      {passwordModalBattle && (
        <PasswordJoinModal
          battle={passwordModalBattle}
          onSubmit={handlePasswordSubmit}
          onCancel={() => {
            setPasswordModalBattle(null);
            setPasswordError(null);
          }}
          isLoading={isPasswordSubmitting}
          error={passwordError}
        />
      )}
    </div>
    </PageAtmosphere>
  );
}
